import PersonPicker from './PersonPicker.jsx';

/* One question, rendered from its catalogue row.
 *
 * Lifted out of NewTicket.jsx on 2026-09-11 so the single-page form and the
 * one-question-at-a-time wizard render questions through the SAME code. Two
 * copies would drift, and the copy nobody was looking at would be the one a
 * requester saw.
 *
 * Every branch here is driven by `field.type`, which is set in
 * app/src/demo/catalog.js - the one and only place the form is described.
 * Adding a question is an edit there; adding a KIND of question is this file.
 */
export default function Field({ field, value, onChange, invalid }) {
  /* Not a question - a red flag raised by an earlier answer, in the flow rather
   * than buried at the end. `warning` carries no value and is never required,
   * so it never reaches the answers or the priority rules. */
  if (field.type === 'warning') {
    return (
      <div className="warnbox" id={`f-${field.key}`} role="note" style={{ marginBottom: 14 }}>
        <span className="material-symbols-outlined" aria-hidden="true">warning</span>
        <span>
          <b>{field.label}</b>
          {field.hint ? <><br />{field.hint}</> : null}
        </span>
      </div>
    );
  }

  /* The responsible-use acknowledgement. Deliberately NOT a normal choice: it
   * is one box that has to be ticked, and the rules are on screen next to it
   * rather than behind a link. An acknowledgement nobody read is worse than
   * none, because it looks like consent.
   *
   * The stored value is the ISO timestamp of the tick, not `true` - so the
   * ticket records WHEN somebody agreed, which is the only part that would
   * matter later. */
  if (field.type === 'ack') {
    const agreed = Boolean(value);
    return (
      <div className="field" id={`f-${field.key}`}>
        <div className={`ackbox${agreed ? ' ackbox--on' : ''}${invalid ? ' ackbox--invalid' : ''}`}>
          <p className="ackbox__title">
            <span className="material-symbols-outlined" aria-hidden="true">verified_user</span>
            {field.label}
          </p>
          <ul className="ackbox__list">
            {(field.points || []).map((point) => <li key={point}>{point}</li>)}
          </ul>
          <button
            type="button"
            className={`ackbox__confirm${agreed ? ' ackbox__confirm--on' : ''}`}
            aria-pressed={agreed}
            onClick={() => onChange(agreed ? '' : new Date().toISOString())}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {agreed ? 'check_box' : 'check_box_outline_blank'}
            </span>
            <span>{field.confirmLabel || 'I understand.'}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field" id={`f-${field.key}`}>
      <span className="field__label">
        {field.label} {field.required ? <span className="req">*</span> : <span className="opt">(optional)</span>}
        {/* Beside the question, not under the options - see FormWizard.jsx. */}
        {field.type === 'multi' ? <span className="field__multi">Select all that apply</span> : null}
      </span>

      {field.type === 'multi' ? (
        /* Check-all-that-apply. The value is an ARRAY, which is why every
         * required-check goes through hasAnswer() - `[]` is truthy. */
        <div className={`opts${invalid ? ' opts--invalid' : ''}`}>
          {field.options.map((o) => {
            const picked = Array.isArray(value) && value.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                className={`o${picked ? ' o--on' : ''}`}
                aria-pressed={picked}
                onClick={() => {
                  const current = Array.isArray(value) ? value : [];
                  onChange(picked ? current.filter((v) => v !== o.value) : [...current, o.value]);
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      ) : field.type === 'choice' ? (
        <div className={`opts${invalid ? ' opts--invalid' : ''}`}>
          {field.options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`o${value === o.value ? ' o--on' : ''}`}
              aria-pressed={value === o.value}
              onClick={() => onChange(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : field.type === 'person' ? (
        <PersonPicker value={value} onChange={onChange} invalid={invalid} />
      ) : field.type === 'textarea' ? (
        <textarea
          className="textarea"
          style={invalid ? { outline: '2px solid var(--ihs-red)' } : undefined}
          maxLength={field.maxLength || 2000 /* the server's cap: api/src/shared/formValidate.js */}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="input"
          style={invalid ? { outline: '2px solid var(--ihs-red)' } : undefined}
          type="text"
          maxLength={field.maxLength || 500 /* the server's cap: api/src/shared/formValidate.js */}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {field.hint ? <span className="field__hint">{field.hint}</span> : null}
    </div>
  );
}
